import { differenceInCalendarDays, startOfDay } from 'date-fns'
import { PermissoesPasta } from '@/core/enums/permissoes-pasta'
import * as z from 'zod'
import { t } from '@lingui/core/macro'
import { NEW_ITEM_MAX_DAYS, VisibilidadePasta } from './constants'
import {
  GruposUsuarioPermissoes,
  ItemBiblioteca,
  ItemDownload,
  NovaPastaInput
} from '@/core/types/graphql'
import { NovaPastaValues } from '../hooks/use-nova-pasta-dialog'
import { ConcederAcessoValues } from '../hooks/use-conceder-acesso-dialog'
import { FileUploader } from '@/core/utils/file-uploader'
import { FileUploadType } from '@/core/enums/file-upload-type'
import { FileUpload } from '@tecsinapse/cortex-react'
import { Metadata } from '@/modules/biblioteca-virtual/types/types'
import { downloadByLink } from '@/core/components/uploader-utils'
import { AxiosInstance } from 'axios'
import { createWriteStream } from 'streamsaver'
import { ZipWriter } from '@zip.js/zip.js'
import React from 'react'

const isLeituraEscrita = (current: GruposUsuarioPermissoes | null) =>
  current?.permissaoAcessoPasta?.id === PermissoesPasta.LEITURA_ESCRITA
const isLeitura = (current: GruposUsuarioPermissoes | null) =>
  current?.permissaoAcessoPasta?.id === PermissoesPasta.LEITURA

export const initialValuesFragment = (pastaAtual: ItemBiblioteca) => {
  const { isPasta, permissoes = [], isPublico } = pastaAtual || {}

  if (!isPasta || !permissoes) {
    return {
      isPublico: VisibilidadePasta.PUBLICA,
      idsGrupoAcesso: [],
      idsGrupoAcessoEscrita: []
    }
  }

  const idsGrupoAcesso = permissoes
    .filter(permissao => isLeitura(permissao))
    .map(permissao => ({
      label: permissao?.grupoUsuarios.nome,
      value: permissao?.grupoUsuarios.id,
      favorite: permissao?.grupoUsuarios.favorito
    }))
  const idsGrupoAcessoEscrita = permissoes
    .filter(permissao => isLeituraEscrita(permissao))
    .map(permissao => ({
      label: permissao?.grupoUsuarios.nome,
      value: permissao?.grupoUsuarios.id,
      favorite: permissao?.grupoUsuarios.favorito
    }))

  return {
    isPublico: isPublico
      ? VisibilidadePasta.PUBLICA
      : VisibilidadePasta.PRIVADA,
    idsGrupoAcesso,
    idsGrupoAcessoEscrita
  }
}

export const mapSubmitValues = (
  submitValues: NovaPastaValues | ConcederAcessoValues
): Omit<NovaPastaInput, 'idPai' | 'nome'> => {
  const objLeitura = (submitValues.idsGrupoAcesso ?? []).map(idGrupoAcesso => ({
    id: idGrupoAcesso.value,
    description: PermissoesPasta.LEITURA
  }))

  const objEscrita = (submitValues.idsGrupoAcessoEscrita || []).map(
    idGrupoAcessoEscrita => ({
      id: idGrupoAcessoEscrita.value,
      description: PermissoesPasta.LEITURA_ESCRITA
    })
  )

  const tiposAcesso = submitValues.isPublico?.value
    ? [...objLeitura, ...objEscrita]
    : []

  return {
    idsGrupoAcesso: tiposAcesso.map(tipoAcesso => tipoAcesso.id),
    tiposAcesso: tiposAcesso.map(tipoAcesso => ({
      idGrupoAcesso: tipoAcesso.id,
      permissao: tipoAcesso.description
    }))
  }
}

export const alterarProprietarioSchema = z.object({
  proprietario: z.email({ error: t`Digite um e-mail válido` })
})

export const concederAcessoSchema = z
  .object({
    isPublico: z
      .object({
        value: z.boolean(),
        label: z.string()
      })
      .nonoptional({ error: t`O campo Visibilidade é obrigatório` }),
    idsGrupoAcesso: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
        favorite: z.boolean()
      })
    ),
    idsGrupoAcessoEscrita: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
        favorite: z.boolean()
      })
    )
  })
  .refine(
    data => {
      if (data.isPublico.value) {
        return (
          data.idsGrupoAcesso.length > 0 ||
          data.idsGrupoAcessoEscrita.length > 0
        )
      }
      return true
    },
    {
      error: t`É obrigatório escolher uma permissão em pastas públicas.`,
      path: ['isPublico'],
      when: payload =>
        novaPastaValidationSchema
          .pick({
            isPublico: true
          })
          .safeParse(payload.value).success
    }
  )

export const novaPastaValidationSchema = concederAcessoSchema.safeExtend({
  nome: z.string().nonempty({ error: t`O campo Nome é obrigatório.` }),
  idPai: z
    .object({
      value: z.string(),
      label: z.string()
    })
    .optional()
})

export const novaSubPastaSchema = z.object({
  nome: z.string().nonempty({ error: t`O campo Nome é obrigatório.` }),
  idPai: z
    .object({
      value: z.string(),
      label: z.string()
    })
    .optional()
})

export const upload = async (
  instance: AxiosInstance,
  file: FileUpload<Metadata>
) => {
  const uploader = new FileUploader(instance, FileUploadType.LIBRARY)

  try {
    await uploader.uploadFromFile(file.file)
    return uploader.representation()
  } catch (e) {
    console.error(e)
    throw e
  }
}

const uniqueZipPath = (zipPath: string, used: Set<string>) => {
  if (!used.has(zipPath)) {
    used.add(zipPath)
    return zipPath
  }

  const lastSlash = zipPath.lastIndexOf('/')
  const dir = lastSlash >= 0 ? zipPath.slice(0, lastSlash + 1) : ''
  const name = lastSlash >= 0 ? zipPath.slice(lastSlash + 1) : zipPath

  const dot = name.lastIndexOf('.')
  const base = dot >= 0 ? name.slice(0, dot) : name
  const ext = dot >= 0 ? name.slice(dot) : ''

  let i = 1
  let candidate = `${dir}${base} (${i})${ext}`
  while (used.has(candidate)) {
    i++
    candidate = `${dir}${base} (${i})${ext}`
  }
  used.add(candidate)
  return candidate
}

export const downloadFiles = async (
  files?: ItemDownload[],
  singleFile?: boolean,
  setIsDownloading?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setIsDownloading?.(true)

  const usedZipPaths = new Set<string>()

  if ((files ?? []).length < 1) {
    setIsDownloading?.(false)
    throw new Error('No files available')
  }

  if (files?.length === 1 && singleFile) {
    downloadByLink(files[0].link)
    setIsDownloading?.(false)
    return
  }

  const fileStream = createWriteStream(
    `download-${new Date().toISOString()}.zip`
  )

  const zipWriter = new ZipWriter(fileStream)

  for await (const file of files ?? []) {
    try {
      const response = await fetch(file.link)

      if (!response.ok) {
        throw new Error(`Failed to fetch ${file.link}: ${response.statusText}`)
      }

      const uniquePath = uniqueZipPath(file.path, usedZipPaths)

      if (!response.body) throw new Error('ReadableStream not supported')

      await zipWriter.add(uniquePath, response.body)
    } catch (error) {
      setIsDownloading?.(false)
      console.error(
        `Error processing file ${file.path.split('/').pop()}:`,
        (error as Error).message
      )
      throw new Error(
        `${(error as Error).message}: ${file.path.split('/').pop()}`
      )
    }
  }

  try {
    await zipWriter.close()
  } catch (error) {
    console.error('Error generating zip file:', (error as Error).message)
    throw new Error(`Error generating zip file: ${(error as Error).message}`)
  } finally {
    setIsDownloading?.(false)
  }
}

export const isNewItem = (dataCriacao: Date) => {
  const hoje = startOfDay(new Date())
  const criacao = startOfDay(dataCriacao)
  return differenceInCalendarDays(hoje, criacao) <= NEW_ITEM_MAX_DAYS
}
